import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { FabricSettings } from '../services/api';
import { sendToMobiPrint } from '../services/printer';

interface InstructionSubStep {
  step_number: number;
  text: string;
  photo_url?: string;
  safety_note?: string;
}

interface WorkstationStep {
  id: string;
  step_number: number;
  step_name: string;
  instruction_steps: InstructionSubStep[];
  quality_checks: string[];
  safety_note?: string;
  tool_requirements?: string;
  progressId?: string;
  progressStatus?: string;
}

interface OrderSpecs {
  width: number | null;
  height: number | null;
  frameColor: string;
  componentColors?: {
    housingColor?: string;
    leftTrackColor?: string;
    rightTrackColor?: string;
    weightBarColor?: string;
    leftUChannelColor?: string;
    rightUChannelColor?: string;
    housingRalNumber?: string;
    leftTrackRalNumber?: string;
    rightTrackRalNumber?: string;
    weightBarRalNumber?: string;
    leftUChannelRalNumber?: string;
    rightUChannelRalNumber?: string;
  } | null;
  fabricType: string;
  fabricColor: string;
  motorType: string;
  motorSide: string;
  uChannel: string;
  specialNotes: string;
  unitNumber: number;
  quantity: number;
}

interface WorkOrder {
  id: string;
  orderId: string;
  orderNumber: string;
  sidemark: string;
  companyName: string;
  itemNumber: number;
  totalItems?: number;
  productType: string;
}

interface CardBomItem {
  partNumber: string;
  description: string;
  quantity: number;
  cutLength?: number | null;
  unit: string;
  componentType: string;
  imageUrl?: string | null;
}

interface CutSizes {
  fabricWidth?: number;
  fabricHeight?: number;
  weightBar?: number;
  rollerTube?: number;
  housingMain?: number;
  housingCover?: number;
  vTrack?: number;
  uChannel?: number;
  kederGuide?: number;
  screwCover?: number;
}

interface StationSpec {
  label: string;
  value: string;
}

interface SpringHoles {
  count: number;
  spacing: number;
  positions: number[];
  positionsFormatted?: string[];
}

interface PositionedPart {
  partNumber: string;
  description: string;
  motorSidePosition: number;
  idlerSidePosition: number;
  effectiveMotorPosition: number;
  effectiveIdlerPosition: number;
  deductionApplied: boolean;
  notes?: string | null;
}

interface PositioningData {
  factoryDeductionTotal: number;
  parts: PositionedPart[];
}

interface CurrentCard {
  workOrder: WorkOrder;
  currentStep: WorkstationStep | null;
  stepProgress: { current: number; total: number };
  orderSpecs: OrderSpecs;
  isRush: boolean;
  noWork?: boolean;
  noStepsConfigured?: boolean;
  bomItems?: CardBomItem[];
  cutSizes?: CutSizes | null;
  stationSpecs?: StationSpec[];
  springHoles?: SpringHoles | null;
  shippingMethod?: string | null;
  positioning?: PositioningData | null;
  trainingModeDefault?: boolean;
  lockTrainingMode?: boolean;
  lockProductionMode?: boolean;
  workstationCode?: string;
  allQualityChecks?: string[];
  allSteps?: StepSummary[];
}

interface StepSummary {
  stepNumber: number;
  stepName: string;
  instructionCount: number;
  qualityCheckCount: number;
  estimatedMinutes: number;
  instructionSteps?: InstructionSubStep[];
  qualityChecks?: string[];
  toolRequirements?: string | null;
  safetyNote?: string | null;
  condition?: string | null;
  applicable?: boolean;
}

interface BreakSession {
  id: string;
  break_type: string;
  started_at: string;
  duration_seconds: number;
}

// Parts Pull types
interface PartsPullItem {
  id: string;
  partNumber: string;
  description: string;
  quantityNeeded: number;
  quantityPulled: number;
  pulled: boolean;
  shortage: boolean;
  shortageQuantity: number | null;
  binLocation: string | null;
  componentType: string;
  imageUrl: string | null;
}

interface PartsPullData {
  id: string;
  orderId: string;
  orderNumber: string;
  pullType: 'bin' | 'shelves';
  status: 'pending' | 'in_progress' | 'complete' | 'shortage';
  binColor: string | null;
  items: PartsPullItem[];
  startedAt: string | null;
  completedAt: string | null;
}

interface PullQueueItem {
  id: string;
  orderId: string;
  orderNumber: string;
  companyName: string;
  sidemark: string;
  pullType: 'bin' | 'shelves';
  status: string;
  unitCount: number;
  itemCount: number;
  isRush: boolean;
}

// Convert decimal inches to fractional format for tape measures
function toFraction(decimal: number): string {
  const whole = Math.floor(decimal);
  const fraction = decimal - whole;
  const sixteenths = Math.round(fraction * 16);
  if (sixteenths === 0) return `${whole}`;
  if (sixteenths === 16) return `${whole + 1}`;
  let num = sixteenths;
  let denom = 16;
  while (num % 2 === 0 && denom > 1) { num /= 2; denom /= 2; }
  return whole === 0 ? `${num}/${denom}` : `${whole} ${num}/${denom}`;
}

// Parts pull workstation codes
const PARTS_PULL_CODES = ['PARTSPULL-BIN', 'PARTSPULL-SHELVES'];

// Workstations that have drop material to label
const DROP_LABEL_CODES = ['SAW1', 'SAW2', 'SAW3', 'SAW4', 'CUTTABLE'];

// Workstation category codes for expert mode
const CUTTING_CODES = ['SAW1', 'SAW2', 'SAW3', 'SAW4'];
const FABRIC_CODES = ['CUTTABLE', 'SEALER', 'CORNERPATCH', 'SEWING', 'FABRICSEAM'];
const ASSEMBLY_CODES = ['BOX-ASSEMBLY', 'ROLLER-ASSEMBLY', 'WB-ASSEMBLY', 'TRACK-ASSEMBLY', 'FINAL-ASSEMBLY'];
const PACKING_CODES = ['PACKING', 'PACKWC', 'SHIPBOX', 'SHIPPACK', 'SHIPSTRAP'];

type WorkMode = 'training' | 'production' | 'expert';

interface WorkCardPageProps {
  // When provided, the component renders in modal mode: route URL is ignored,
  // sibling nav swaps the inner state instead of navigating, and post-completion
  // calls onClose() instead of redirecting to /queue.
  modalWsId?: string;
  modalWoId?: string;
  onClose?: () => void;
  onWorkOrderChange?: (woId: string) => void;
}

export function WorkCardPage({ modalWsId, modalWoId, onClose, onWorkOrderChange }: WorkCardPageProps = {}) {
  const inModalMode = !!onClose;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Derive the raw strings. The searchParams OBJECT gets a new identity on many
  // re-renders, so effects must depend on these stable strings instead — keying
  // on the object made the "restore station" effect re-fire and clobber a
  // station the operator had just picked (SAW4 snapping back to SAW3).
  const wsParam = searchParams.get('ws');
  const woParam = searchParams.get('wo');
  // Internal state for the current wo when in modal mode — lets sibling nav swap
  // the active card without changing URL. Falls back to the modalWoId prop.
  const [modalCurrentWoId, setModalCurrentWoId] = useState<string | undefined>(modalWoId);
  useEffect(() => { setModalCurrentWoId(modalWoId); }, [modalWoId]);
  // Helper: in modal mode, swap card by setting state; otherwise navigate.
  const goToWorkOrder = useCallback((woId: string) => {
    if (inModalMode) {
      setModalCurrentWoId(woId);
      onWorkOrderChange?.(woId);
    } else {
      navigate(`/work-cards?ws=${modalWsId || searchParams.get('ws') || ''}&wo=${woId}`);
    }
  }, [inModalMode, navigate, modalWsId, searchParams, onWorkOrderChange]);
  // Helper: in modal mode, "back to queue" closes the modal; otherwise navigate.
  const exitToQueue = useCallback(() => {
    if (inModalMode) {
      onClose?.();
    } else {
      const ws = searchParams.get('ws');
      navigate(ws ? `/queue?ws=${ws}` : '/queue');
    }
  }, [inModalMode, onClose, searchParams, navigate]);
  // The page uses `fixed inset-0` as its outer wrapper so it covers the
  // viewport when mounted at /work-cards. Inside the modal that ignores
  // the modal container and busts out — so swap the anchor for a contained
  // layout. Each wrapper keeps its own bg/flex classes.
  const pageAnchor = inModalMode ? 'relative w-full min-h-full' : 'fixed inset-0';
  const [workstationId, setWorkstationId] = useState<string>('');
  const [workstations, setWorkstations] = useState<any[]>([]);
  const [card, setCard] = useState<CurrentCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeBreak, setActiveBreak] = useState<BreakSession | null>(null);
  const [showQualityModal, setShowQualityModal] = useState(false);
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [qualityChecks, setQualityChecks] = useState<Record<string, boolean>>({});
  const [completedToday, setCompletedToday] = useState(0);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showNavMenu, setShowNavMenu] = useState(false);
  const [workMode, setWorkMode] = useState<WorkMode>('training');
  const [productionStepChecks, setProductionStepChecks] = useState<Record<number, boolean>>({});
  const [completingAll, setCompletingAll] = useState(false);
  const [siblingWos, setSiblingWos] = useState<{ id: string; itemNumber: number; status: string }[]>([]);

  // Parts Pull state
  const [pullData, setPullData] = useState<PartsPullData | null>(null);
  const [pullLoading, setPullLoading] = useState(false);
  const [pullQueue, setPullQueue] = useState<PullQueueItem[]>([]);
  const [pullQueueLoading, setPullQueueLoading] = useState(false);
  const [selectedPullOrderId, setSelectedPullOrderId] = useState<string | null>(null);
  const [showShortageModal, setShowShortageModal] = useState(false);
  const [shortageItem, setShortageItem] = useState<PartsPullItem | null>(null);
  const [shortageQty, setShortageQty] = useState(0);
  const [shortageNotes, setShortageNotes] = useState('');
  const [showCompletePullModal, setShowCompletePullModal] = useState(false);
  const [labelPrinting, setLabelPrinting] = useState(false);
  const [showDropModal, setShowDropModal] = useState(false);

  // QC Photo state
  const [qcPhotos, setQcPhotos] = useState<{ url: string; publicId: string }[]>([]);
  const [qcUploading, setQcUploading] = useState(false);
  const qcFileInputRef = useRef<HTMLInputElement>(null);

  // Fabric settings state (for fabric stations)
  const [fabricSettings, setFabricSettings] = useState<FabricSettings | null>(null);

  // Derived mode booleans for backwards compat
  const trainingMode = workMode === 'training';
  const expertMode = workMode === 'expert';

  // Determine if current workstation is a parts pull station
  const currentWs = workstations.find((w) => w.id === workstationId);
  const isPartsPullStation = currentWs && PARTS_PULL_CODES.includes(currentWs.code);
  const pullType: 'bin' | 'shelves' = currentWs?.code === 'PARTSPULL-SHELVES' ? 'shelves' : 'bin';
  const wsCode = card?.workstationCode || currentWs?.code || '';
  const isFabricStation = FABRIC_CODES.includes(wsCode) || wsCode.includes('FAB');
  const isLastUnit = card?.workOrder?.totalItems
    ? card.workOrder.itemNumber === card.workOrder.totalItems
    : true; // Default to show QC if data missing (safety)

  // Load workstations
  useEffect(() => {
    loadWorkstations();
  }, []);

  // Load current card when workstation selected (skip for parts pull stations)
  useEffect(() => {
    if (workstationId) {
      if (!isPartsPullStation) {
        loadCurrentCard();
      }
      loadProgress();
      checkActiveBreak();
      if (!inModalMode) {
        localStorage.setItem('selected_workstation', workstationId);
      }
    }
  }, [workstationId, isPartsPullStation, wsParam, woParam, modalCurrentWoId, inModalMode]);

  // Load parts pull queue when parts pull station selected
  useEffect(() => {
    if (isPartsPullStation) {
      loadPullQueue();
    } else {
      setPullQueue([]);
      setSelectedPullOrderId(null);
      setPullData(null);
    }
  }, [isPartsPullStation, pullType]);

  // Load parts pull items when an order is selected
  useEffect(() => {
    if (isPartsPullStation && selectedPullOrderId) {
      loadPartsPullData(selectedPullOrderId);
    } else {
      setPullData(null);
    }
  }, [isPartsPullStation, selectedPullOrderId]);

  // Restore selected workstation (modal prop > query param > saved)
  useEffect(() => {
    if (modalWsId) {
      setWorkstationId(modalWsId);
      return;
    }
    if (wsParam) {
      setWorkstationId(wsParam);
      localStorage.setItem('selected_workstation', wsParam);
      return;
    }
    // No ws in the URL. Only fall back to the saved station when nothing is
    // selected yet — otherwise this would overwrite a pick the operator just
    // made in the station picker (which clears the URL param).
    setWorkstationId(prev => prev || localStorage.getItem('selected_workstation') || '');
  }, [wsParam, modalWsId]);

  // Load fabric settings when at a fabric station
  useEffect(() => {
    if (card && isFabricStation && card.orderSpecs?.fabricType) {
      api.getFabricSettings(card.orderSpecs.fabricType)
        .then(settings => setFabricSettings(settings))
        .catch(() => setFabricSettings(null));
    } else {
      setFabricSettings(null);
    }
  }, [card?.orderSpecs?.fabricType, isFabricStation]);

  const loadWorkstations = async () => {
    try {
      const response = await api.get('/production/workstations');
      setWorkstations(response.data);
    } catch (error) {
      console.error('Failed to load workstations:', error);
    }
  };

  const loadPullQueue = async () => {
    setPullQueueLoading(true);
    try {
      const response = await api.get(`/parts-pull/queue/${pullType}`);
      setPullQueue(response.data);
    } catch (error) {
      console.error('Failed to load pull queue:', error);
    } finally {
      setPullQueueLoading(false);
      setLoading(false);
    }
  };

  const loadCurrentCard = async () => {
    setLoading(true);
    try {
      const targetWo = inModalMode ? modalCurrentWoId : woParam;
      const url = targetWo
        ? `/production/workstation/current-card/${workstationId}?wo=${targetWo}`
        : `/production/workstation/current-card/${workstationId}`;
      const response = await api.get(url);
      setCard(response.data);
      // Load sibling work orders for same order at same station (for unit toggle)
      if (response.data?.workOrder?.orderId && response.data?.workOrder?.totalItems > 1) {
        try {
          const sibRes = await api.get(`/production/workstation/siblings/${workstationId}/${response.data.workOrder.orderId}`);
          setSiblingWos(sibRes.data || []);
        } catch { setSiblingWos([]); }
      } else {
        setSiblingWos([]);
      }
      // Initialize quality checks for training mode (per-step)
      if (response.data?.currentStep?.quality_checks) {
        const checks: Record<string, boolean> = {};
        response.data.currentStep.quality_checks.forEach((c: string) => {
          checks[c] = false;
        });
        setQualityChecks(checks);
      }
      // Reset step checkoffs for production mode
      setProductionStepChecks({});
      // Set work mode: lock overrides everything, then localStorage, then API default
      const empId = user?.employeeId || user?.id;
      if (response.data?.lockTrainingMode) {
        setWorkMode('training');
      } else if (response.data?.lockProductionMode) {
        // Locked to the simplified step list — no training or expert view.
        setWorkMode('production');
      } else {
        const storedMode = localStorage.getItem(`workstation_mode_${empId}`);
        if (storedMode && ['training', 'production', 'expert'].includes(storedMode)) {
          setWorkMode(storedMode as WorkMode);
        } else {
          // Migrate old boolean key
          const oldKey = localStorage.getItem(`workstation_training_mode_${empId}`);
          if (oldKey !== null) {
            const mode = JSON.parse(oldKey) ? 'training' : 'production';
            setWorkMode(mode as WorkMode);
            localStorage.setItem(`workstation_mode_${empId}`, mode);
            localStorage.removeItem(`workstation_training_mode_${empId}`);
          } else if (response.data?.trainingModeDefault !== undefined) {
            setWorkMode(response.data.trainingModeDefault ? 'training' : 'production');
          }
        }
      }
    } catch (error) {
      console.error('Failed to load card:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPartsPullData = async (orderId: string) => {
    setPullLoading(true);
    try {
      const response = await api.get(`/orders/${orderId}/parts-pull`);
      const summary = response.data;
      const pull = pullType === 'bin' ? summary.bin : summary.shelves;

      // Auto-start pull if still pending
      if (pull && pull.status === 'pending') {
        try {
          await api.post(`/orders/${orderId}/parts-pull/${pullType}/start`, {});
          const updated = await api.get(`/orders/${orderId}/parts-pull`);
          const updatedPull = pullType === 'bin' ? updated.data.bin : updated.data.shelves;
          setPullData(updatedPull || pull);
        } catch {
          setPullData(pull);
        }
      } else {
        setPullData(pull || null);
      }
    } catch (error) {
      console.error('Failed to load parts pull:', error);
    } finally {
      setPullLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      const response = await api.get(`/production/workstation/progress/${workstationId}`);
      setCompletedToday(response.data.completedToday || 0);
    } catch (error) {
      console.error('Failed to load progress:', error);
    }
  };

  const checkActiveBreak = async () => {
    try {
      const response = await api.get('/production/workstation/break/current');
      setActiveBreak(response.data?.active ? response.data : null);
    } catch (error) {
      console.error('Failed to check break:', error);
    }
  };

  const handleCompleteStep = async () => {
    if (!card?.currentStep) return;

    try {
      const response = await api.post('/production/workstation/complete-step', {
        work_order_id: card.workOrder.id,
        step_id: card.currentStep.id,
        quality_checks: qualityChecks,
      });

      setShowQualityModal(false);
      setCompletedToday((prev) => prev + 1);
      setQcPhotos([]); // Reset QC photos for next step

      // If all units in the order are done at this station, go back to queue
      if (response.data?.orderComplete) {
        showToast('Order complete at this station!', 'success');
        exitToQueue();
        return;
      }

      setCard(response.data);
      showToast(`Step complete! ${completedToday + 1} done today`, 'success');

      // Reset quality checks for next step
      if (response.data?.currentStep?.quality_checks) {
        const checks: Record<string, boolean> = {};
        response.data.currentStep.quality_checks.forEach((c: string) => {
          checks[c] = false;
        });
        setQualityChecks(checks);
      }
    } catch (error) {
      console.error('Failed to complete step:', error);
      showToast('Failed to complete step', 'error');
    }
  };

  const handleSkipStep = async (reason: string) => {
    if (!card?.currentStep) return;

    try {
      const response = await api.post('/production/workstation/skip-step', {
        work_order_id: card.workOrder.id,
        step_id: card.currentStep.id,
        reason,
      });

      setShowSkipModal(false);
      setCard(response.data);
      showToast('Step skipped', 'success');
    } catch (error) {
      console.error('Failed to skip step:', error);
      showToast('Failed to skip step', 'error');
    }
  };

  const handleRestartStep = async () => {
    if (!card?.workOrder) return;

    try {
      const body: any = { work_order_id: card.workOrder.id };
      // In training mode, restart the specific current step if it's completed/skipped
      if (card.currentStep?.progressStatus === 'completed' || card.currentStep?.progressStatus === 'skipped') {
        body.step_id = card.currentStep.id;
      }
      // Otherwise omit step_id — API will find the last completed/skipped step

      const response = await api.post('/production/workstation/restart-step', body);

      setCard(response.data);
      setCompletedToday((prev) => Math.max(0, prev - 1));
      // Reset production mode checkmarks
      setProductionStepChecks({});
      showToast('Step restarted', 'success');
    } catch (error) {
      console.error('Failed to restart step:', error);
      showToast('Failed to restart step', 'error');
    }
  };

  const handleFlagStep = async (reason: string, notes: string) => {
    if (!card?.currentStep) return;

    try {
      const response = await api.post('/production/workstation/flag-step', {
        work_order_id: card.workOrder.id,
        step_id: card.currentStep.id,
        reason,
        notes,
      });

      setShowProblemModal(false);
      setCard(response.data);
      showToast('Issue flagged - supervisor notified', 'success');
    } catch (error) {
      console.error('Failed to flag step:', error);
      showToast('Failed to flag step', 'error');
    }
  };

  // QC Photo capture handler
  const handleQcPhotoCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !card?.workOrder) return;

    setQcUploading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || '/api';
      const formData = new FormData();
      formData.append('file', file);
      formData.append('order_id', card.workOrder.orderId || card.workOrder.order_id);

      // Upload photo to Cloudinary
      const uploadRes = await fetch(`${API_URL}/qc-records/upload-photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: formData,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const uploadData = await uploadRes.json();

      // Create QC record
      const recordRes = await api.post('/qc-records', {
        order_id: card.workOrder.orderId || card.workOrder.order_id,
        work_order_id: card.workOrder.id,
        workstation_code: currentWs?.code || null,
        step_number: card.currentStep?.step_number || null,
        step_name: card.currentStep?.step_name || null,
        record_type: 'photo',
        image_url: uploadData.url,
        image_public_id: uploadData.publicId,
      });

      setQcPhotos(prev => [...prev, { url: uploadData.url, publicId: uploadData.publicId }]);
      showToast('QC photo saved', 'success');
    } catch (error) {
      console.error('QC photo upload failed:', error);
      showToast('Failed to upload photo', 'error');
    } finally {
      setQcUploading(false);
      // Reset file input so same file can be selected again
      if (qcFileInputRef.current) qcFileInputRef.current.value = '';
    }
  };

  const cycleWorkMode = () => {
    if (card?.lockTrainingMode || card?.lockProductionMode) return;
    const modes: WorkMode[] = ['training', 'production', 'expert'];
    const nextIdx = (modes.indexOf(workMode) + 1) % modes.length;
    const next = modes[nextIdx];
    setWorkMode(next);
    const empId = user?.employeeId || user?.id;
    localStorage.setItem(`workstation_mode_${empId}`, next);
  };

  const handleCompleteAllSteps = async () => {
    if (!card?.workOrder) return;
    setCompletingAll(true);
    try {
      const response = await api.post('/production/workstation/complete-all-steps', {
        work_order_id: card.workOrder.id,
        quality_checks: productionStepChecks,
      });
      setCompletedToday((prev) => prev + 1);

      // If all units in the order are done at this station, go back to queue
      if (response.data?.orderComplete) {
        showToast('Order complete at this station!', 'success');
        exitToQueue();
        return;
      }

      setCard(response.data);
      showToast('Unit complete!', 'success');
      // Reset step checks for next card
      setProductionStepChecks({});
    } catch (error) {
      console.error('Failed to complete all steps:', error);
      showToast('Failed to complete', 'error');
    } finally {
      setCompletingAll(false);
    }
  };

  const handlePrintLabel = async () => {
    if (!card?.workOrder?.id || !currentWs) return;
    setLabelPrinting(true);
    try {
      const labelType = await api.getWorkstationLabelType(currentWs.code);
      const result = await api.queueAndPrintLabel(card.workOrder.id, labelType, workstationId);
      showToast(result.success ? 'Label queued' : result.message, result.success ? 'success' : 'error');
    } catch (error: any) {
      console.error('Failed to print label:', error);
      showToast(error.message || 'Failed to print label', 'error');
    } finally {
      setLabelPrinting(false);
    }
  };

  // Parts Pull handlers
  const handleMarkPulled = async (item: PartsPullItem) => {
    if (!pullData || !selectedPullOrderId) return;
    try {
      await api.post(`/parts-pull/items/${item.id}/pulled`, {
        quantity: item.quantityNeeded,
      });
      // Update item in-place to avoid scroll-to-top on full reload
      setPullData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((i) =>
            i.id === item.id
              ? { ...i, pulled: true, quantityPulled: item.quantityNeeded }
              : i
          ),
        };
      });
      showToast(`${item.partNumber} pulled`, 'success');
    } catch (error) {
      console.error('Failed to mark pulled:', error);
      showToast('Failed to mark item pulled', 'error');
    }
  };

  const handleOpenShortage = (item: PartsPullItem) => {
    setShortageItem(item);
    setShortageQty(item.quantityNeeded - item.quantityPulled);
    setShortageNotes('');
    setShowShortageModal(true);
  };

  const handleSubmitShortage = async () => {
    if (!shortageItem || !pullData || !selectedPullOrderId) return;
    try {
      await api.post(`/parts-pull/items/${shortageItem.id}/shortage`, {
        shortageQuantity: shortageQty,
        notes: shortageNotes,
      });
      // Update item in-place to avoid scroll-to-top on full reload
      const pulledQty = shortageItem.quantityNeeded - shortageQty;
      setPullData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((i) =>
            i.id === shortageItem.id
              ? { ...i, pulled: true, shortage: true, shortageQuantity: shortageQty, quantityPulled: pulledQty }
              : i
          ),
        };
      });
      setShowShortageModal(false);
      setShortageItem(null);
      showToast('Shortage reported', 'success');
    } catch (error) {
      console.error('Failed to report shortage:', error);
      showToast('Failed to report shortage', 'error');
    }
  };

  const handleCompletePull = async (binColor: 'black' | 'red') => {
    if (!pullData || !selectedPullOrderId) return;
    try {
      // Complete the parts pull
      await api.post(`/orders/${selectedPullOrderId}/parts-pull/${pullType}/complete`, {
        binColor,
      });

      setShowCompletePullModal(false);
      setCompletedToday((prev) => prev + 1);
      setPullData(null);
      setSelectedPullOrderId(null);
      showToast(`Pull complete - ${binColor.toUpperCase()} bin`, 'success');

      // Refresh queue to remove completed order
      loadPullQueue();
    } catch (error) {
      console.error('Failed to complete pull:', error);
      showToast('Failed to complete pull', 'error');
    }
  };

  const handleStartBreak = async (breakType: string, reason?: string) => {
    try {
      const response = await api.post('/production/workstation/break/start', {
        type: breakType,
        workstation_id: workstationId,
        reason,
      });
      setActiveBreak(response.data);
    } catch (error) {
      console.error('Failed to start break:', error);
    }
  };

  const handleEndBreak = async () => {
    try {
      await api.post('/production/workstation/break/end');
      setActiveBreak(null);
      showToast('Back to work!', 'success');
    } catch (error) {
      console.error('Failed to end break:', error);
    }
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const getImageUrl = (url: string | undefined | null) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `https://res.cloudinary.com/keder-screens/image/upload/${url}`;
  };

  // Parts pull progress
  const pullProgress = useCallback(() => {
    if (!pullData) return { pulled: 0, total: 0, percentage: 0 };
    const pulled = pullData.items.filter((i) => i.pulled || i.shortage).length;
    const total = pullData.items.length;
    return { pulled, total, percentage: total > 0 ? Math.round((pulled / total) * 100) : 0 };
  }, [pullData]);

  // Group parts pull items by component type
  const groupedPullItems = useCallback(() => {
    if (!pullData) return {};
    return pullData.items.reduce(
      (acc, item) => {
        const type = item.componentType || 'Other';
        if (!acc[type]) acc[type] = [];
        acc[type].push(item);
        return acc;
      },
      {} as Record<string, PartsPullItem[]>,
    );
  }, [pullData]);

  // Break overlay
  if (activeBreak) {
    const breakMinutes = Math.floor(activeBreak.duration_seconds / 60);
    return (
      <div className={`${pageAnchor} bg-gray-900 flex flex-col items-center justify-center p-6`}>
        <div className="text-6xl mb-4">
          {activeBreak.break_type === 'lunch' ? '🍽️' : activeBreak.break_type === 'break' ? '☕' : activeBreak.break_type === 'pause' ? '⏸️' : '🚻'}
        </div>
        <h1 className="text-3xl font-bold text-white mb-2 capitalize">{activeBreak.break_type}</h1>
        {activeBreak.reason && (
          <p className="text-gray-400 mb-2 capitalize">{activeBreak.reason}</p>
        )}
        <div className="text-6xl font-bold text-yellow-400 mb-8">{breakMinutes} min</div>
        <button
          onClick={handleEndBreak}
          className="px-8 py-4 bg-green-600 text-white text-xl font-bold rounded-xl hover:bg-green-700"
        >
          Back to Work
        </button>
      </div>
    );
  }

  // Workstation selector
  if (!workstationId) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-white mb-6">Select Your Workstation</h1>
        <div className="grid gap-4">
          {workstations.map((ws) => (
            <button
              key={ws.id}
              onClick={() => {
                setWorkstationId(ws.id);
                // Sync the URL to the pick. Without this the URL kept the old
                // ws (and a stale wo), and the restore effect snapped the
                // operator back to the previous station — picking "Saw 4 -
                // Weight Bar" landed on Saw 3 showing the roller tube.
                if (!inModalMode) setSearchParams({ ws: ws.id }, { replace: true });
              }}
              className="p-6 bg-gray-800 rounded-xl text-left hover:bg-gray-700 transition-colors"
            >
              <div className="text-xl font-bold text-white">{ws.name}</div>
              <div className="text-gray-400">{ws.code}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Loading state
  if (loading && !isPartsPullStation) {
    return (
      <div className={`${pageAnchor} bg-gray-900 flex items-center justify-center`}>
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // No work available (non-parts-pull stations only)
  if (!isPartsPullStation && card?.noWork) {
    return (
      <div className={`${pageAnchor} bg-gray-900 flex flex-col`}>
        <div className="bg-gray-800 px-4 py-3 flex gap-3">
          <button
            onClick={() => {
              setWorkstationId('');
              if (!inModalMode) setSearchParams({}, { replace: true });
            }}
            className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600"
          >
            Stations
          </button>
          <button
            onClick={() => navigate('/parts-pull')}
            className="px-3 py-2 bg-yellow-600 text-white rounded-lg text-sm hover:bg-yellow-500 font-medium"
          >
            Parts Pull
          </button>
          <button
            onClick={() => navigate('/')}
            className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600"
          >
            Dashboard
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-white mb-2">No Tasks Available</h1>
          <p className="text-gray-400 text-center mb-8">Check with your supervisor for more work</p>
          <button onClick={loadCurrentCard} className="px-6 py-3 bg-blue-600 text-white rounded-xl">
            Refresh
          </button>
        </div>
        <BreakButtons onBreak={handleStartBreak} />
      </div>
    );
  }

  // =====================================================
  // PARTS PULL WORKSTATION - QUEUE VIEW
  // =====================================================
  if (isPartsPullStation && !selectedPullOrderId) {
    return (
      <div className={`${pageAnchor} bg-gray-900 flex flex-col`}>
        {/* Header */}
        <div className="bg-gray-800 px-4 py-3 sticky top-0 z-10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button onClick={() => setShowNavMenu(!showNavMenu)} className="text-gray-400 hover:text-white p-1">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">{currentWs?.name || 'Parts Pull'}</h1>
                <div className="text-gray-400 text-sm">{pullQueue.length} order{pullQueue.length !== 1 ? 's' : ''} waiting</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-green-400 font-bold">{completedToday} done today</div>
            </div>
          </div>
        </div>

        {/* Quick Nav Menu */}
        {showNavMenu && (
          <div className="bg-gray-800 border-t border-gray-700 px-4 py-2 flex gap-2 flex-wrap z-20">
            <button
              onClick={() => { setWorkstationId(''); setShowNavMenu(false); if (!inModalMode) setSearchParams({}, { replace: true }); }}
              className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Stations
            </button>
            <button
              onClick={() => { navigate('/parts-pull'); setShowNavMenu(false); }}
              className="px-3 py-2 bg-yellow-600 text-white rounded-lg text-sm hover:bg-yellow-500 font-medium"
            >
              Parts Pull Tab
            </button>
            <button
              onClick={() => { navigate('/'); setShowNavMenu(false); }}
              className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600"
            >
              Dashboard
            </button>
          </div>
        )}

        {/* Queue List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {pullQueueLoading ? (
            <div className="text-center py-12 text-gray-400">Loading orders...</div>
          ) : pullQueue.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">✅</div>
              <div className="text-xl text-white font-bold mb-2">No Orders Waiting</div>
              <div className="text-gray-400 mb-6">All {pullType} pulls are complete</div>
              <button
                onClick={loadPullQueue}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl"
              >
                Refresh
              </button>
            </div>
          ) : (
            pullQueue.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedPullOrderId(item.orderId)}
                className={`w-full p-4 rounded-xl text-left transition-colors ${
                  item.isRush
                    ? 'bg-red-900 border-2 border-red-500'
                    : item.status === 'in_progress'
                      ? 'bg-yellow-900/30 border border-yellow-700'
                      : 'bg-gray-800 hover:bg-gray-750'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-lg font-bold text-white">#{item.orderNumber}</div>
                    <div className="text-gray-400">{item.companyName}</div>
                    {item.sidemark && (
                      <div className="text-gray-500 text-sm">{item.sidemark}</div>
                    )}
                  </div>
                  <div className="text-right">
                    {item.isRush && (
                      <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded mb-1 inline-block">
                        RUSH
                      </span>
                    )}
                    {item.status === 'in_progress' && (
                      <span className="px-2 py-1 bg-yellow-600 text-white text-xs font-bold rounded mb-1 inline-block">
                        IN PROGRESS
                      </span>
                    )}
                    <div className="text-gray-400 text-sm">
                      {item.unitCount} unit{item.unitCount !== 1 ? 's' : ''} &bull; {item.itemCount} parts
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Break Buttons */}
        <BreakButtons onBreak={handleStartBreak} />

        {/* Toast */}
        {toast && (
          <div
            className={`fixed bottom-24 left-4 right-4 p-4 rounded-xl shadow-lg z-50 ${
              toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
            } text-white text-center font-medium`}
          >
            {toast.message}
          </div>
        )}
      </div>
    );
  }

  // =====================================================
  // PARTS PULL WORKSTATION - ITEMS VIEW (order selected)
  // =====================================================
  if (isPartsPullStation && selectedPullOrderId) {
    const progress = pullProgress();
    const groups = groupedPullItems();
    const hasShortages = pullData ? pullData.items.some((i) => i.shortage) : false;
    const allComplete = pullData ? progress.pulled === progress.total : false;

    return (
      <div className={`${pageAnchor} bg-gray-900 flex flex-col`}>
        {/* Top Bar */}
        <div className="bg-gray-800 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setSelectedPullOrderId(null); setPullData(null); loadPullQueue(); }}
              className="text-gray-400 hover:text-white p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <div className="text-white font-bold">#{pullData?.orderNumber || '...'}</div>
              <div className="text-gray-400 text-sm uppercase">{pullType} Pull</div>
            </div>
          </div>
          {pullData && (
            <div className="text-right">
              <div className="text-yellow-400 font-bold">
                {progress.pulled}/{progress.total}
              </div>
              <div className="text-gray-500 text-xs">{progress.percentage}% complete</div>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {pullData && (
          <div className="h-2 bg-gray-700">
            <div
              className={`h-full transition-all ${hasShortages ? 'bg-red-500' : 'bg-green-500'}`}
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        )}

        {/* Parts Pull Items List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {pullLoading || !pullData ? (
            <div className="text-center py-8 text-gray-400">Loading parts...</div>
          ) : (
            Object.entries(groups).map(([type, items]) => (
              <div key={type}>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 px-1">{type}</div>
                <div className="space-y-2">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className={`rounded-xl p-3 transition-all ${
                        item.pulled
                          ? 'bg-green-900/50 border border-green-700'
                          : item.shortage
                            ? 'bg-red-900/50 border border-red-700'
                            : 'bg-gray-800'
                      }`}
                    >
                      <div className="flex gap-3">
                        {/* Image or placeholder */}
                        <div className="w-16 h-16 flex-shrink-0 bg-gray-700 rounded-lg overflow-hidden flex items-center justify-center">
                          {item.imageUrl ? (
                            <img
                              src={getImageUrl(item.imageUrl)!}
                              alt={item.partNumber}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-2xl">{item.pulled ? '✓' : item.shortage ? '!' : '📦'}</span>
                          )}
                        </div>

                        {/* Part info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="text-white font-bold">{item.partNumber}</div>
                              <div className="text-gray-400 text-sm truncate">{item.description}</div>
                            </div>
                            <div className="text-right flex-shrink-0 ml-2">
                              <div className="text-lg font-bold text-yellow-400">x{item.quantityNeeded}</div>
                              {item.binLocation && <div className="text-xs text-gray-500">{item.binLocation}</div>}
                            </div>
                          </div>

                          {/* Status or action buttons */}
                          {item.pulled ? (
                            <div className="mt-2 text-green-400 text-sm font-medium flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Pulled
                            </div>
                          ) : item.shortage ? (
                            <div className="mt-2 text-red-400 text-sm font-medium">
                              Shortage: {item.shortageQuantity} missing
                            </div>
                          ) : (
                            <div className="mt-2 flex gap-2">
                              <button
                                onClick={() => handleMarkPulled(item)}
                                className="flex-1 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 active:bg-green-800"
                              >
                                Pull
                              </button>
                              <button
                                onClick={() => handleOpenShortage(item)}
                                className="py-2 px-3 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 active:bg-red-800"
                              >
                                Shortage
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Bottom Action */}
        {pullData && (
          <div className="bg-gray-800 p-4">
            <button
              onClick={() => setShowCompletePullModal(true)}
              disabled={!allComplete}
              className={`w-full py-4 text-white text-lg font-bold rounded-xl transition-colors ${
                allComplete
                  ? hasShortages
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                  : 'bg-gray-600 cursor-not-allowed'
              }`}
            >
              {allComplete
                ? hasShortages
                  ? 'Complete with Shortages'
                  : 'Complete Pull'
                : `${progress.total - progress.pulled} items remaining`}
            </button>
          </div>
        )}

        {/* Break Buttons */}
        <BreakButtons onBreak={handleStartBreak} />

        {/* Shortage Modal */}
        {showShortageModal && shortageItem && (
          <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-white mb-4">Report Shortage</h2>
              <div className="mb-4">
                <div className="text-gray-400 text-sm">Part</div>
                <div className="text-white font-bold">{shortageItem.partNumber}</div>
                <div className="text-gray-400 text-sm">{shortageItem.description}</div>
              </div>
              <div className="mb-4">
                <label className="text-gray-400 text-sm block mb-1">How many are missing?</label>
                <input
                  type="number"
                  value={shortageQty}
                  onChange={(e) => setShortageQty(parseInt(e.target.value) || 0)}
                  min={1}
                  max={shortageItem.quantityNeeded}
                  className="w-full p-3 bg-gray-700 text-white rounded-lg"
                />
                <div className="text-gray-500 text-xs mt-1">Needed: {shortageItem.quantityNeeded}</div>
              </div>
              <div className="mb-4">
                <label className="text-gray-400 text-sm block mb-1">Notes (optional)</label>
                <textarea
                  value={shortageNotes}
                  onChange={(e) => setShortageNotes(e.target.value)}
                  placeholder="e.g., Only found 2 in bin"
                  className="w-full p-3 bg-gray-700 text-white rounded-lg"
                  rows={2}
                />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowShortageModal(false)} className="flex-1 py-3 bg-gray-600 text-white rounded-xl">
                  Cancel
                </button>
                <button onClick={handleSubmitShortage} className="flex-1 py-3 bg-red-600 text-white font-bold rounded-xl">
                  Report Shortage
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Complete Pull Modal */}
        {showCompletePullModal && pullData && (
          <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-white mb-2">Complete Pull</h2>
              {pullType === 'shelves' ? (
                <>
                  <p className="text-gray-400 mb-6">
                    {hasShortages
                      ? 'This pull has shortages. Parts will be flagged for supervisor review.'
                      : 'All parts pulled successfully and placed on shelf.'}
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => handleCompletePull(hasShortages ? 'red' : 'black')}
                      className={`w-full py-4 ${hasShortages ? 'bg-yellow-600 hover:bg-yellow-500' : 'bg-green-600 hover:bg-green-500'} text-white font-bold rounded-xl flex items-center justify-center gap-3`}
                    >
                      {hasShortages ? (
                        <>
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          Confirm - Flag Shortages
                        </>
                      ) : (
                        <>
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Confirm - Parts on Shelf
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setShowCompletePullModal(false)}
                      className="w-full py-3 bg-gray-600 text-white rounded-xl"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-400 mb-6">
                    {hasShortages
                      ? 'This pull has shortages. Select RED bin to flag for supervisor review.'
                      : 'All parts pulled successfully. Select BLACK bin for staging.'}
                  </p>
                  <div className="space-y-3">
                    {!hasShortages && (
                      <button
                        onClick={() => handleCompletePull('black')}
                        className="w-full py-4 bg-gray-700 text-white font-bold rounded-xl flex items-center justify-center gap-3 hover:bg-gray-600"
                      >
                        <div className="w-8 h-8 bg-gray-900 rounded-full border-2 border-gray-500" />
                        BLACK Bin - All Good
                      </button>
                    )}
                    {hasShortages && (
                      <button
                        onClick={() => handleCompletePull('red')}
                        className="w-full py-4 bg-red-900 text-white font-bold rounded-xl flex items-center justify-center gap-3 hover:bg-red-800"
                      >
                        <div className="w-8 h-8 bg-red-600 rounded-full border-2 border-red-400" />
                        RED Bin - Has Shortages
                      </button>
                    )}
                    <button
                      onClick={() => setShowCompletePullModal(false)}
                      className="w-full py-3 bg-gray-600 text-white rounded-xl"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div
            className={`fixed bottom-24 left-4 right-4 p-4 rounded-xl shadow-lg z-50 ${
              toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
            } text-white text-center font-medium`}
          >
            {toast.message}
          </div>
        )}
      </div>
    );
  }

  // =====================================================
  // REGULAR WORKSTATION VIEW (non-parts-pull)
  // =====================================================
  const allStepsDone = workMode === 'production' && card?.allSteps
    ? card.allSteps.filter(s => s.applicable !== false).every((s) => productionStepChecks[s.stepNumber])
    : true;

  // This work order is already finished (viewing a sibling that's done). The
  // COMPLETE button must reflect that so workers don't re-hit it in a loop or
  // double-make a unit that's already built.
  const isCompleted = (card as any)?.isCompleted === true
    || card?.workOrder?.status === 'completed'
    || card?.workOrder?.status === 'skipped';

  return (
    <div className={`${pageAnchor} bg-gray-900 flex flex-col`}>
      {/* Top Bar — mode toggle + counter kept in modal mode; nav / station
          switcher hidden since the modal has its own header for those. */}
      <div className="bg-gray-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {!inModalMode && (
            <button onClick={() => setShowNavMenu(!showNavMenu)} className="text-gray-400 hover:text-white p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          {!inModalMode && (
            <div>
              <div className="text-white font-bold">{user?.name}</div>
              <div className="text-gray-400 text-sm">{workstations.find((w) => w.id === workstationId)?.name}</div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Work Mode Toggle (Training → Production → Expert) */}
          <button
            onClick={cycleWorkMode}
            disabled={card?.lockTrainingMode || card?.lockProductionMode}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors ${
              workMode === 'training'
                ? 'bg-blue-600 text-white'
                : workMode === 'production'
                  ? 'bg-yellow-500 text-gray-900'
                  : 'bg-green-600 text-white'
            } ${(card?.lockTrainingMode || card?.lockProductionMode) ? 'opacity-60 cursor-not-allowed' : ''}`}
            title={card?.lockTrainingMode ? 'Training mode is locked by admin' : card?.lockProductionMode ? 'Production mode is locked by admin' : ''}
          >
            {workMode === 'training' ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Training
              </>
            ) : workMode === 'production' ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Production
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                Expert
              </>
            )}
          </button>
          <div className="text-right">
            <div className="text-green-400 font-bold text-sm">{completedToday} done</div>
            {trainingMode && card?.stepProgress && (
              <div className="text-gray-400 text-xs">
                Step {card.stepProgress.current}/{card.stepProgress.total}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Nav Menu — hidden inside the modal (nav out of the modal
          would leave the underlying page stranded) */}
      {!inModalMode && showNavMenu && (
        <div className="bg-gray-800 border-t border-gray-700 px-4 py-2 flex gap-2 flex-wrap z-20">
          <button
            onClick={() => { setWorkstationId(''); setShowNavMenu(false); if (!inModalMode) setSearchParams({}, { replace: true }); }}
            className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Stations
          </button>
          <button
            onClick={() => { navigate('/parts-pull'); setShowNavMenu(false); }}
            className="px-3 py-2 bg-yellow-600 text-white rounded-lg text-sm hover:bg-yellow-500 flex items-center gap-2 font-medium"
          >
            Parts Pull
          </button>
          <button
            onClick={() => { navigate('/queue'); setShowNavMenu(false); }}
            className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600"
          >
            My Queue
          </button>
          <button
            onClick={() => { navigate('/'); setShowNavMenu(false); }}
            className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600"
          >
            Dashboard
          </button>
          <button
            onClick={() => { navigate('/scan'); setShowNavMenu(false); }}
            className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600"
          >
            Scan
          </button>
        </div>
      )}

      {/* Progress Bar (training mode only) */}
      {trainingMode && card?.stepProgress && card.stepProgress.total > 0 && (
        <div className="h-2 bg-gray-700">
          <div
            className="h-full bg-green-500 transition-all"
            style={{
              width: `${((card.stepProgress.current - 1) / card.stepProgress.total) * 100}%`,
            }}
          />
        </div>
      )}

      {/* Card Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Order Info */}
        <div className={`rounded-xl p-4 mb-4 ${card?.isRush ? 'bg-red-900 border-2 border-red-500' : 'bg-gray-800'}`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="text-2xl font-bold text-white">Order #{card?.workOrder.orderNumber}</div>
              <div className="text-gray-400">
                {card?.workOrder.companyName} - {card?.workOrder.sidemark}
              </div>
              <button
                type="button"
                onClick={() => navigate(`/report-problem?ws=${workstationId}&wo=${card?.workOrder?.id || ''}&order=${(card?.workOrder as any)?.orderId || ''}&orderNumber=${encodeURIComponent(card?.workOrder?.orderNumber || '')}`)}
                className="mt-2 inline-flex items-center gap-1 text-sm text-amber-300 hover:text-amber-200 underline"
              >
                Report a problem with this order
              </button>
            </div>
            <div className="flex flex-col items-end gap-1">
              {card?.isRush && (
                <span className="px-3 py-1 bg-red-600 text-white font-bold rounded-full animate-pulse">RUSH</span>
              )}
              {card?.workOrder.itemNumber && card.workOrder.totalItems > 1 && siblingWos.length > 1 ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      const idx = siblingWos.findIndex(s => s.id === card!.workOrder.id);
                      if (idx > 0) {
                        goToWorkOrder(siblingWos[idx - 1].id);
                      }
                    }}
                    disabled={siblingWos.findIndex(s => s.id === card.workOrder.id) <= 0}
                    className="px-3 py-1 bg-gray-700 text-white rounded-l-full text-lg disabled:opacity-30"
                  >
                    &lt;
                  </button>
                  <span className="px-4 py-1 bg-yellow-500 text-gray-900 font-bold text-lg">
                    {(card.workOrder as any)?.productType === 'part' ? 'Part' : 'Screen'} {card.workOrder.itemNumber} of {card.workOrder.totalItems}
                  </span>
                  <button
                    onClick={() => {
                      const idx = siblingWos.findIndex(s => s.id === card!.workOrder.id);
                      if (idx < siblingWos.length - 1) {
                        goToWorkOrder(siblingWos[idx + 1].id);
                      }
                    }}
                    disabled={siblingWos.findIndex(s => s.id === card.workOrder.id) >= siblingWos.length - 1}
                    className="px-3 py-1 bg-gray-700 text-white rounded-r-full text-lg disabled:opacity-30"
                  >
                    &gt;
                  </button>
                </div>
              ) : card?.workOrder.itemNumber ? (
                <span className="px-3 py-1 bg-yellow-500 text-gray-900 font-bold rounded-full text-lg">
                  {(card.workOrder as any)?.productType === 'part' ? 'Part' : 'Screen'} {card.workOrder.itemNumber}{card.workOrder.totalItems ? ` of ${card.workOrder.totalItems}` : ''}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Parts cut banner — for cut-to-length parts orders (U-channel, track, etc.)
            the operator just needs part + length + qty, not screen sections. */}
        {(card?.workOrder as any)?.productType === 'part' && (() => {
          const ps = (card?.workOrder as any)?.specs || {};
          return (
            <div className="bg-gray-800 rounded-xl p-4 mb-4 border-2 border-yellow-400/60">
              <h3 className="text-yellow-400 text-sm font-bold mb-3">CUT LIST</h3>
              <div className="text-2xl font-bold text-white mb-3">{ps.partName || 'Part'}</div>
              <div className={`grid ${ps.fabricHeight != null ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
                <div className="bg-yellow-900/30 rounded-lg p-3 text-center">
                  <div className="text-4xl font-bold text-yellow-400">{(ps.partLength ?? ps.fabricCut) ?? '—'}"</div>
                  <div className="text-xs text-gray-400 mt-1">{ps.fabricCut != null ? 'FABRIC WIDTH' : 'CUT LENGTH'}</div>
                </div>
                {ps.fabricHeight != null && (
                  <div className="bg-yellow-900/30 rounded-lg p-3 text-center">
                    <div className="text-4xl font-bold text-yellow-400">{ps.fabricHeight}"</div>
                    <div className="text-xs text-gray-400 mt-1">FABRIC HEIGHT</div>
                  </div>
                )}
                <div className="bg-yellow-900/30 rounded-lg p-3 text-center">
                  <div className="text-4xl font-bold text-yellow-400">{ps.partQuantity ?? 1}</div>
                  <div className="text-xs text-gray-400 mt-1">PIECES</div>
                </div>
              </div>
              {ps.partColor && (
                <div className="mt-3 text-base text-gray-300">Color: <span className="text-white font-semibold capitalize">{ps.partColor}</span></div>
              )}
              {ps.seam && (
                <div className="mt-3 inline-block bg-red-900 border border-red-500 rounded px-3 py-1 text-sm font-bold text-red-200">SEAM REQUIRED — 2 panels</div>
              )}
              <div className="mt-2 text-xs text-gray-500">{ps.fabricCut != null ? 'Fabric cut width already includes allowance.' : 'Finished cut size — do not add allowance.'}</div>
            </div>
          );
        })()}

        {/* Station-Specific Specs (replaces generic SPECIFICATIONS) */}
        {card?.stationSpecs && card.stationSpecs.length > 0 && (
          <div className="bg-gray-800 rounded-xl p-4 mb-4">
            <h3 className="text-gray-400 text-sm font-medium mb-3">SPECIFICATIONS</h3>
            <div className="grid grid-cols-2 gap-3">
              {card.stationSpecs.map((spec, idx) => (
                <div
                  key={idx}
                  className={`rounded-lg p-3 ${
                    spec.label === 'SEAM' && spec.value === 'REQUIRED'
                      ? 'bg-red-900 border border-red-500'
                      : spec.label.includes('MIXED COLORS')
                        ? 'bg-orange-900 border-2 border-orange-400'
                        : spec.label.includes('BIAS CUT')
                          ? 'bg-purple-900 border-2 border-purple-400'
                          : spec.value.includes('"')
                            ? 'bg-blue-900'
                            : 'bg-gray-700'
                  }`}
                >
                  <div className={`text-xs ${
                    spec.label === 'SEAM' && spec.value === 'REQUIRED' ? 'text-red-300'
                      : spec.label.includes('MIXED COLORS') ? 'text-orange-300 font-bold'
                      : spec.label.includes('BIAS CUT') ? 'text-purple-300 font-bold'
                      : spec.value.includes('"') ? 'text-blue-300' : 'text-gray-400'
                  }`}>{spec.label}</div>
                  <div className={`font-bold ${
                    spec.label === 'SEAM' && spec.value === 'REQUIRED' ? 'text-red-100 text-lg'
                      : spec.label.includes('MIXED COLORS') ? 'text-orange-100 text-lg'
                      : spec.label.includes('BIAS CUT') ? 'text-purple-100 text-lg'
                      : 'text-white'
                  }`}>{spec.value}</div>
                  {/* Tape-measure cross-check — when the value is a single
                      measurement like 58.5", append the fractional equivalent
                      below in grey. Workers dial decimals into the machine
                      and verify with a tape after. Only renders when value
                      matches a single decimal-inches pattern (skips colors,
                      labels, multi-value strings). */}
                  {(() => {
                    const m = String(spec.value).trim().match(/^(-?\d+(?:\.\d+)?)\s*"$/);
                    if (!m) return null;
                    const dec = parseFloat(m[1]);
                    if (!Number.isFinite(dec)) return null;
                    const frac = toFraction(dec);
                    // Skip if fraction string equals the decimal display (whole numbers)
                    if (frac === m[1] || frac === String(Math.round(dec))) return null;
                    return (
                      <div className="text-white/90 font-mono text-base font-medium mt-0.5">({frac}")</div>
                    );
                  })()}
                </div>
              ))}
            </div>
            {card?.orderSpecs.specialNotes && (
              <div className="mt-3 p-3 bg-yellow-900 rounded-lg">
                <div className="text-yellow-300 text-xs">NOTES</div>
                <div className="text-yellow-100">{card.orderSpecs.specialNotes}</div>
              </div>
            )}
          </div>
        )}

        {/* Fabric Weld/Cut Settings (for fabric stations) */}
        {isFabricStation && fabricSettings && (
          <div className="bg-gray-800 rounded-xl p-4 mb-4 border-2 border-purple-500/50">
            <h3 className="text-purple-400 text-sm font-bold mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
              </svg>
              WELD / CUT SETTINGS — {fabricSettings.fabricType}
            </h3>
            <div className="mb-3">
              <div className="text-gray-400 text-xs mb-2">WELD SETTINGS</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-purple-900/30 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-400">
                    {fabricSettings.weldTempHot != null ? `${fabricSettings.weldTempHot}s` : '--'}
                  </div>
                  <div className="text-xs text-gray-400">Hot Time</div>
                </div>
                <div className="bg-purple-900/30 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-400">
                    {fabricSettings.weldTempCold != null ? `${fabricSettings.weldTempCold}s` : '--'}
                  </div>
                  <div className="text-xs text-gray-400">Cold Time</div>
                </div>
                <div className="bg-purple-900/30 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-purple-400">
                    {fabricSettings.weldSpeed ?? '--'}
                  </div>
                  <div className="text-xs text-gray-400">Speed</div>
                </div>
                <div className="bg-purple-900/30 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-purple-400">
                    {fabricSettings.weldPressure ?? '--'}
                  </div>
                  <div className="text-xs text-gray-400">Pressure</div>
                </div>
              </div>
              {fabricSettings.weldNotes && (
                <div className="mt-2 p-2 bg-purple-900/20 rounded-lg text-sm text-purple-200">
                  <strong>Notes:</strong> {fabricSettings.weldNotes}
                </div>
              )}
            </div>
            <div>
              <div className="text-gray-400 text-xs mb-2">CUT TABLE SETTINGS</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-purple-900/30 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-yellow-400">
                    {fabricSettings.cutBladeType || '--'}
                  </div>
                  <div className="text-xs text-gray-400">Blade Type</div>
                </div>
                <div className="bg-purple-900/30 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-yellow-400">
                    {fabricSettings.cutSpeed ?? '--'}
                  </div>
                  <div className="text-xs text-gray-400">Speed</div>
                </div>
                <div className="bg-purple-900/30 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-yellow-400">
                    {fabricSettings.cutPressure ?? '--'}
                  </div>
                  <div className="text-xs text-gray-400">Pressure</div>
                </div>
                <div className="bg-purple-900/30 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-yellow-400">
                    {fabricSettings.cutPasses ?? '--'}
                  </div>
                  <div className="text-xs text-gray-400">Passes</div>
                </div>
              </div>
              {fabricSettings.cutNotes && (
                <div className="mt-2 p-2 bg-purple-900/20 rounded-lg text-sm text-purple-200">
                  <strong>Notes:</strong> {fabricSettings.cutNotes}
                </div>
              )}
            </div>
          </div>
        )}
        {isFabricStation && !fabricSettings && card?.orderSpecs?.fabricType && (
          <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-gray-600/50">
            <div className="text-center text-gray-400 py-2">
              <p>No weld/cut settings found for: <span className="text-yellow-400 font-medium">{card.orderSpecs.fabricType}</span></p>
              <p className="text-sm mt-1">Contact admin to add settings for this fabric type.</p>
            </div>
          </div>
        )}

        {/* Component Colors Detail — when mixed colors on order. Labels EVERY
            part with its ACTUAL color (override, else frame color) so the floor
            never has to infer "frame black means housing + weight bar are black."
            U-channels only shown when the order has them. */}
        {card?.orderSpecs?.componentColors && Object.keys(card.orderSpecs.componentColors).length > 0 && (() => {
          const cc = card.orderSpecs.componentColors;
          const fc = card.orderSpecs.frameColor || '';
          const isOpenRoll = String((card.orderSpecs as any).systemType || '').toLowerCase() === 'open_roll';
          const uChannel = String((card.orderSpecs as any).uChannel || 'none').toLowerCase();
          const hasUChannel = uChannel !== 'none' && uChannel !== '';
          const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
          // Resolve a part's color: its override, else the frame color.
          const resolve = (override: string | undefined, ralKey: string) => {
            const color = override || fc;
            if (color === 'custom' && cc[ralKey as keyof typeof cc]) return `Custom (${cc[ralKey as keyof typeof cc]})`;
            return cap(color);
          };
          const parts: { label: string; value: string }[] = [];
          if (!isOpenRoll) parts.push({ label: 'Housing (Box)', value: resolve(cc.housingColor, 'housingRalNumber') });
          parts.push({ label: 'Weight Bar', value: resolve(cc.weightBarColor, 'weightBarRalNumber') });
          parts.push({ label: 'Left Track', value: resolve(cc.leftTrackColor, 'leftTrackRalNumber') });
          parts.push({ label: 'Right Track', value: resolve(cc.rightTrackColor, 'rightTrackRalNumber') });
          if (hasUChannel || cc.leftUChannelColor) parts.push({ label: 'Left U-Channel', value: resolve(cc.leftUChannelColor, 'leftUChannelRalNumber') });
          if (hasUChannel || cc.rightUChannelColor) parts.push({ label: 'Right U-Channel', value: resolve(cc.rightUChannelColor, 'rightUChannelRalNumber') });
          return (
          <div className="bg-orange-900 border-2 border-orange-400 rounded-xl p-4 mb-4">
            <h3 className="text-orange-300 text-sm font-bold mb-3">⚠️ MIXED COMPONENT COLORS — EACH PART'S COLOR IS LISTED BELOW</h3>
            <div className="grid grid-cols-2 gap-2">
              {parts.map((p) => (
                <div key={p.label} className="bg-orange-800 rounded-lg p-2">
                  <div className="text-orange-300 text-xs">{p.label}</div>
                  <div className="text-white font-bold">{p.value}</div>
                </div>
              ))}
            </div>
          </div>
          );
        })()}

        {/* Drop Allocations — USE EXISTING MATERIAL */}
        {card?.dropAllocations && card.dropAllocations.length > 0 && (
          <div className="bg-green-900 border-2 border-green-400 rounded-xl p-4 mb-4">
            <h3 className="text-green-300 text-sm font-bold mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
              USE EXISTING DROP — DO NOT CUT FROM NEW STOCK
            </h3>
            {card.dropAllocations.map((drop: any, idx: number) => (
              <div key={idx} className="bg-green-800 rounded-lg p-3 mb-2 last:mb-0">
                <div className="text-green-100 font-bold text-xl">{drop.dropId}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                  <span className="text-green-200 text-sm font-medium">BIN: {drop.binLocation || 'Check inventory'}</span>
                  <span className="text-green-200 text-sm">Drop: {drop.dropLength}"</span>
                  <span className="text-green-200 text-sm">Cut to: {drop.cutLength}"</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ======= TRAINING MODE: Full SOP step-by-step ======= */}
        {trainingMode && (
          <>
            {/* Step Name */}
            {card?.currentStep && (
              <>
                <div className="bg-gray-800 rounded-xl p-4 mb-4">
                  <div className="text-gray-400 text-sm mb-1">CURRENT STEP</div>
                  <div className="text-3xl font-bold text-white">{card.currentStep.step_name}</div>
                  {card.currentStep.tool_requirements && (
                    <div className="mt-2 text-gray-400">Tools: {card.currentStep.tool_requirements}</div>
                  )}
                </div>

                {/* Safety Warning */}
                {card.currentStep.safety_note && (
                  <div className="bg-orange-900 border border-orange-500 rounded-xl p-4 mb-4 flex items-start gap-3">
                    <span className="text-2xl">⚠️</span>
                    <div className="text-orange-100">{card.currentStep.safety_note}</div>
                  </div>
                )}

                {/* Instructions */}
                {card.currentStep.instruction_steps && card.currentStep.instruction_steps.length > 0 && (
                  <div className="bg-gray-800 rounded-xl p-4 mb-4">
                    <div className="text-gray-400 text-sm mb-3">INSTRUCTIONS</div>
                    <div className="space-y-4">
                      {card.currentStep.instruction_steps.map((step, index) => (
                        <div key={index} className="flex gap-4">
                          <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-blue-600 rounded-full text-white font-bold">
                            {step.step_number}
                          </div>
                          <div className="flex-1">
                            <div className="text-white">{step.text}</div>
                            {step.photo_url ? (
                              <img
                                src={getImageUrl(step.photo_url)!}
                                alt={`Step ${step.step_number}`}
                                className="mt-2 rounded-lg max-h-48 object-contain"
                              />
                            ) : (
                              <div className="mt-2 w-full h-28 bg-gray-700 rounded-lg border-2 border-dashed border-gray-600 flex flex-col items-center justify-center">
                                <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-gray-500 text-xs mt-1">Step {step.step_number} photo</span>
                              </div>
                            )}
                            {step.safety_note && (
                              <div className="mt-2 text-orange-400 text-sm flex items-center gap-1">
                                ⚠️ {step.safety_note}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* No steps configured */}
            {card?.noStepsConfigured && (
              <div className="bg-yellow-900 rounded-xl p-4 mb-4">
                <div className="text-yellow-100">
                  No step instructions configured for this workstation. Complete the work order using standard procedures.
                </div>
              </div>
            )}
          </>
        )}

        {/* ======= PRODUCTION MODE: Step checklist + Complete ======= */}
        {workMode === 'production' && (
          <>
            {card?.allSteps && card.allSteps.length > 0 && (() => {
              const applicableSteps = card.allSteps.filter(s => s.applicable !== false);
              const checkedCount = applicableSteps.filter(s => productionStepChecks[s.stepNumber]).length;
              return (
                <div className="bg-gray-800 rounded-xl overflow-hidden mb-4">
                  <div className="px-4 py-3 border-b border-gray-700">
                    <span className="text-yellow-400 font-semibold text-sm">
                      STEPS ({checkedCount}/{applicableSteps.length})
                    </span>
                  </div>
                  <div className="divide-y divide-gray-700">
                    {card.allSteps.map((step) => {
                      const isApplicable = step.applicable !== false;
                      const checked = isApplicable ? !!productionStepChecks[step.stepNumber] : false;
                      return (
                        <label
                          key={step.stepNumber}
                          className={`flex items-center gap-4 px-4 py-4 transition-colors ${
                            !isApplicable
                              ? 'opacity-35 cursor-default'
                              : checked
                                ? 'bg-gray-700/50 cursor-pointer active:bg-gray-700'
                                : 'cursor-pointer active:bg-gray-700'
                          }`}
                        >
                          <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full font-bold text-sm ${
                            !isApplicable
                              ? 'bg-gray-600 text-gray-400'
                              : checked
                                ? 'bg-green-600 text-white'
                                : 'bg-yellow-600 text-white'
                          }`}>
                            {!isApplicable ? '—' : checked ? '✓' : step.stepNumber}
                          </div>
                          <div className="flex-1">
                            <span className={`font-medium ${
                              !isApplicable ? 'text-gray-600' : checked ? 'text-gray-500 line-through' : 'text-white'
                            }`}>
                              {step.stepName}
                            </span>
                            {!isApplicable && (
                              <span className="text-gray-600 text-xs ml-2">N/A</span>
                            )}
                          </div>
                          {isApplicable ? (
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => setProductionStepChecks(prev => ({ ...prev, [step.stepNumber]: e.target.checked }))}
                              className="w-7 h-7 rounded accent-green-500"
                            />
                          ) : (
                            <div className="w-7 h-7" />
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </>
        )}

        {/* Spring Holes — TRACKDRILL station. Always shown regardless of
            expert/step-by-step mode; this IS the primary measurement the
            drill press operator needs.
            The drill press takes decimal inches as input — NOT fractions —
            so positions render as raw decimals (trailing zeros trimmed),
            with the fractional equivalent as small grey reference text. */}
        {wsCode === 'TRACKDRILL' && card?.springHoles && (() => {
          const sh = card.springHoles as any;
          const holeCount = sh.holes ?? sh.count ?? sh.positions?.length ?? 0;
          if (holeCount <= 0) return null;
          const positions: number[] = Array.isArray(sh.positions) ? sh.positions : [];
          const fmtDec = (n: number) => {
            const s = n.toFixed(4);
            return s.replace(/\.?0+$/, '');
          };
          return (
            <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-blue-500/50">
              <h3 className="text-blue-400 text-sm font-bold mb-3">SPRING HOLE POSITIONS</h3>
              <div className="flex items-center gap-4 mb-3">
                <div className="bg-blue-900 rounded-lg px-3 py-2">
                  <div className="text-blue-300 text-xs">HOLES</div>
                  <div className="text-white font-bold text-xl">{holeCount}</div>
                </div>
                <div className="bg-blue-900 rounded-lg px-3 py-2">
                  <div className="text-blue-300 text-xs">SPACING</div>
                  <div className="text-white font-bold text-xl">{sh.spacing != null ? `${fmtDec(sh.spacing)}"` : '—'}</div>
                  {sh.spacing != null && (
                    <div className="text-white/90 font-mono text-base font-medium">({toFraction(sh.spacing)}")</div>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                {positions.map((pos, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-blue-900/50 rounded px-3 py-2">
                    <span className="text-blue-300 text-sm w-8">#{idx + 1}</span>
                    <span className="text-white font-mono font-bold text-lg">{fmtDec(pos)}"</span>
                    <span className="text-white/90 font-mono text-base font-medium">({toFraction(pos)}")</span>
                    <span className="text-blue-400 text-xs ml-auto">from top</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ======= EXPERT MODE: Station data only, no steps ======= */}
        {expertMode && (
          <>

            {/* Part Positioning — ROLLER-ASSEMBLY (octagonal plug positions) */}
            {card?.positioning && card.positioning.parts.length > 0 && (
              <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-teal-500/50">
                <h3 className="text-teal-400 text-sm font-bold mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  PART POSITIONING
                </h3>
                {card.positioning.factoryDeductionTotal > 0 && (
                  <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-3 mb-3">
                    <div className="text-sm text-yellow-300">
                      Factory Deduction Applied: <span className="font-bold">{card.positioning.factoryDeductionTotal}"</span>
                      <span className="text-yellow-400/80 ml-2">(positions adjusted outward)</span>
                    </div>
                  </div>
                )}
                <div className="space-y-3">
                  {card.positioning.parts.map((part, idx) => (
                    <div key={idx} className="bg-gray-700 rounded-lg p-3">
                      <div className="font-semibold text-white text-sm mb-2">{part.description}</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-teal-900/30 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-teal-400">{toFraction(part.effectiveMotorPosition)}"</div>
                          <div className="text-sm text-gray-400">Motor Side</div>
                          {part.deductionApplied && part.effectiveMotorPosition !== part.motorSidePosition && (
                            <div className="text-xs text-gray-500 mt-1">(base: {toFraction(part.motorSidePosition)}")</div>
                          )}
                        </div>
                        <div className="bg-teal-900/30 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-teal-400">{toFraction(part.effectiveIdlerPosition)}"</div>
                          <div className="text-sm text-gray-400">Idler Side</div>
                          {part.deductionApplied && part.effectiveIdlerPosition !== part.idlerSidePosition && (
                            <div className="text-xs text-gray-500 mt-1">(base: {toFraction(part.idlerSidePosition)}")</div>
                          )}
                        </div>
                      </div>
                      {part.notes && (
                        <div className="text-sm text-gray-400 mt-2 italic">{part.notes}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Shipping Method — Packing/Shipping stations.
                DB stores 'shipping' / 'will_call' / 'willcall'. Anything other than
                will_call is freight; older code compared to 'freight' which never
                matched and always rendered WILL CALL on freight orders. */}
            {PACKING_CODES.includes(wsCode) && (() => {
              const sm = card?.shippingMethod;
              const isWillCall = sm === 'will_call' || sm === 'willcall';
              return (
              <div className="bg-gray-800 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className={`px-4 py-2 rounded-lg font-bold text-lg ${
                    isWillCall ? 'bg-yellow-500 text-gray-900' : 'bg-purple-600 text-white'
                  }`}>
                    {isWillCall ? 'WILL CALL' : 'FREIGHT'}
                  </div>
                  {card?.workOrder.totalItems && (
                    <div className="bg-gray-700 rounded-lg px-3 py-2">
                      <div className="text-gray-400 text-xs">UNITS</div>
                      <div className="text-white font-bold">{card.workOrder.totalItems}</div>
                    </div>
                  )}
                </div>
              </div>
              );
            })()}

            {/* BOM Items — always visible in expert mode (not collapsible).
                Hidden on FINAL-ASSEMBLY + QC: those are visual-check stations
                where the worker isn't pulling parts, and the list just adds
                noise. Same exclusion on the collapsible (training-mode) block
                below. */}
            {card?.bomItems && card.bomItems.length > 0 && wsCode !== 'FINAL-ASSEMBLY' && wsCode !== 'QC' && (
              <div className="bg-gray-800 rounded-xl p-4 mb-4">
                <h3 className="text-green-400 text-sm font-semibold mb-3">PARTS ({card.bomItems.length})</h3>
                <div className="space-y-2">
                  {card.bomItems.map((item, idx) => (
                    <div key={idx} className="flex gap-3 bg-gray-700 rounded-lg p-3">
                      <div className="w-12 h-12 flex-shrink-0 bg-gray-600 rounded overflow-hidden flex items-center justify-center">
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.partNumber} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-gray-400 text-[9px] text-center leading-tight">{item.componentType.replace(/_/g, ' ')}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-bold text-sm">{item.partNumber}</div>
                        <div className="text-gray-400 text-xs truncate">{item.description}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {item.cutLength ? (
                          <div className="text-yellow-400 font-bold">{item.cutLength}"</div>
                        ) : (
                          <div className="text-white font-bold">x{item.quantity}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Part Positioning — training/production mode (expert mode has its own above) */}
        {!expertMode && card?.positioning && card.positioning.parts.length > 0 && (
          <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-teal-500/50">
            <h3 className="text-teal-400 text-sm font-bold mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              PART POSITIONING
            </h3>
            {card.positioning.factoryDeductionTotal > 0 && (
              <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-3 mb-3">
                <div className="text-sm text-yellow-300">
                  Factory Deduction Applied: <span className="font-bold">{card.positioning.factoryDeductionTotal}"</span>
                  <span className="text-yellow-400/80 ml-2">(positions adjusted outward)</span>
                </div>
              </div>
            )}
            <div className="space-y-3">
              {card.positioning.parts.map((part, idx) => (
                <div key={idx} className="bg-gray-700 rounded-lg p-3">
                  <div className="font-semibold text-white text-sm mb-2">{part.description}</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-teal-900/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-teal-400">{toFraction(part.effectiveMotorPosition)}"</div>
                      <div className="text-sm text-gray-400">Motor Side</div>
                      {part.deductionApplied && part.effectiveMotorPosition !== part.motorSidePosition && (
                        <div className="text-xs text-gray-500 mt-1">(base: {toFraction(part.motorSidePosition)}")</div>
                      )}
                    </div>
                    <div className="bg-teal-900/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-teal-400">{toFraction(part.effectiveIdlerPosition)}"</div>
                      <div className="text-sm text-gray-400">Idler Side</div>
                      {part.deductionApplied && part.effectiveIdlerPosition !== part.idlerSidePosition && (
                        <div className="text-xs text-gray-500 mt-1">(base: {toFraction(part.idlerSidePosition)}")</div>
                      )}
                    </div>
                  </div>
                  {part.notes && (
                    <div className="text-sm text-gray-400 mt-2 italic">{part.notes}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Collapsible Cut Sizes (training mode only — collapsed, useful as reference) */}
        {trainingMode && card?.cutSizes && Object.values(card.cutSizes).some(v => v != null && v > 0) && (
          <details className="bg-gray-800 rounded-xl mb-4 border border-yellow-400/30">
            <summary className="p-4 text-yellow-400 font-semibold text-sm cursor-pointer select-none">
              ALL CUT SIZES
            </summary>
            <div className="px-4 pb-4 grid grid-cols-2 gap-2">
              {[
                { key: 'fabricWidth', label: 'Fabric W' },
                { key: 'fabricHeight', label: 'Fabric H' },
                { key: 'housingMain', label: 'Housing' },
                { key: 'housingCover', label: 'Hsg Cover' },
                { key: 'vTrack', label: 'V-Track' },
                { key: 'rollerTube', label: 'Roller Tube' },
                { key: 'weightBar', label: 'Weight Bar' },
                { key: 'uChannel', label: 'U-Channel' },
                { key: 'kederGuide', label: 'Keder Guide' },
                { key: 'screwCover', label: 'Screw Cover' },
              ].filter(({ key }) => (card.cutSizes as any)[key] != null && (card.cutSizes as any)[key] > 0)
                .map(({ key, label }) => (
                <div key={key} className="flex justify-between bg-gray-700 rounded px-3 py-2">
                  <span className="text-gray-400 text-sm">{label}</span>
                  <span className="text-yellow-400 font-mono font-bold text-sm">{(card.cutSizes as any)[key]}"</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Collapsible Parts (training mode only — production mode shows them open above).
            Same FINAL-ASSEMBLY / QC exclusion as the expert-mode block. */}
        {trainingMode && card?.bomItems && card.bomItems.length > 0 && wsCode !== 'FINAL-ASSEMBLY' && wsCode !== 'QC' && (
          <details className="bg-gray-800 rounded-xl mb-4 border border-green-400/30">
            <summary className="p-4 text-green-400 font-semibold text-sm cursor-pointer select-none">
              PARTS ({card.bomItems.length})
            </summary>
            <div className="px-4 pb-4 space-y-2">
              {card.bomItems.map((item, idx) => (
                <div key={idx} className="flex gap-3 bg-gray-700 rounded-lg p-3">
                  <div className="w-12 h-12 flex-shrink-0 bg-gray-600 rounded overflow-hidden flex items-center justify-center">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.partNumber} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-gray-400 text-[9px] text-center leading-tight">{item.componentType.replace(/_/g, ' ')}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-bold text-sm">{item.partNumber}</div>
                    <div className="text-gray-400 text-xs truncate">{item.description}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {item.cutLength ? (
                      <div className="text-yellow-400 font-bold">{item.cutLength}"</div>
                    ) : (
                      <div className="text-white font-bold">x{item.quantity}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Bottom Action Buttons */}
      {trainingMode ? (
        /* Training Mode: step-by-step actions */
        <div className="bg-gray-800 p-4 space-y-3">
          <button
            onClick={() => {
              if (card?.currentStep?.quality_checks?.length && isLastUnit) {
                setShowQualityModal(true);
              } else {
                handleCompleteStep();
              }
            }}
            className="w-full py-5 bg-green-600 text-white text-xl font-bold rounded-xl hover:bg-green-700 flex items-center justify-center gap-2"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
            DONE
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => setShowProblemModal(true)}
              className="flex-1 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 text-sm"
            >
              Problem
            </button>
            <button
              onClick={handlePrintLabel}
              disabled={labelPrinting}
              className="flex-1 py-3 bg-yellow-500 text-gray-900 font-medium rounded-xl hover:bg-yellow-400 disabled:opacity-50 flex items-center justify-center gap-1 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              {labelPrinting ? '...' : 'Print'}
            </button>
            {currentWs && DROP_LABEL_CODES.includes(currentWs.code) && (
              <button
                onClick={() => setShowDropModal(true)}
                className="flex-1 py-3 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-500 flex items-center justify-center gap-1 text-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.477l-3.763 1.105 1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.789l1.599.799L9 4.323V3a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Label Drop
              </button>
            )}
            <button
              onClick={() => qcFileInputRef.current?.click()}
              disabled={qcUploading}
              className="flex-1 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-500 disabled:opacity-50 flex items-center justify-center gap-1 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {qcUploading ? '...' : 'QC Photo'}
            </button>
            <button
              onClick={handleRestartStep}
              className="flex-1 py-3 bg-orange-600 text-white font-medium rounded-xl hover:bg-orange-500 text-sm"
            >
              Restart
            </button>
            <button
              onClick={() => setShowSkipModal(true)}
              className="flex-1 py-3 bg-gray-600 text-white font-medium rounded-xl hover:bg-gray-500 text-sm"
            >
              Skip
            </button>
          </div>
          {/* QC Photo Thumbnails */}
          {qcPhotos.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {qcPhotos.map((photo, i) => (
                <img key={i} src={photo.url} alt={`QC ${i + 1}`} className="w-16 h-16 rounded-lg object-cover flex-shrink-0 border-2 border-green-500" />
              ))}
              <span className="text-green-400 text-xs self-center ml-1">{qcPhotos.length} photo{qcPhotos.length !== 1 ? 's' : ''} saved</span>
            </div>
          )}
        </div>
      ) : expertMode ? (
        /* Expert Mode: COMPLETE always enabled, no step gates */
        <div className="bg-gray-800 p-4 space-y-3">
          <button
            onClick={handleCompleteAllSteps}
            disabled={completingAll || isCompleted}
            className={`w-full py-6 text-white text-2xl font-bold rounded-xl flex items-center justify-center gap-3 transition-colors ${
              isCompleted
                ? 'bg-green-800 cursor-not-allowed'
                : completingAll
                  ? 'bg-green-700'
                  : 'bg-green-600 hover:bg-green-700 active:bg-green-800'
            }`}
          >
            {completingAll ? (
              'Completing...'
            ) : (
              <>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                {isCompleted ? 'COMPLETED' : 'COMPLETE'}
              </>
            )}
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => setShowProblemModal(true)}
              className="flex-1 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 text-sm"
            >
              Problem
            </button>
            <button
              onClick={handlePrintLabel}
              disabled={labelPrinting}
              className="flex-1 py-3 bg-yellow-500 text-gray-900 font-medium rounded-xl hover:bg-yellow-400 disabled:opacity-50 flex items-center justify-center gap-1 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              {labelPrinting ? '...' : 'Print'}
            </button>
            {currentWs && DROP_LABEL_CODES.includes(currentWs.code) && (
              <button
                onClick={() => setShowDropModal(true)}
                className="flex-1 py-3 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-500 flex items-center justify-center gap-1 text-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.477l-3.763 1.105 1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.789l1.599.799L9 4.323V3a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Label Drop
              </button>
            )}
            <button
              onClick={() => qcFileInputRef.current?.click()}
              disabled={qcUploading}
              className="flex-1 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-500 disabled:opacity-50 flex items-center justify-center gap-1 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {qcUploading ? '...' : 'QC Photo'}
            </button>
            <button
              onClick={handleRestartStep}
              className="flex-1 py-3 bg-orange-600 text-white font-medium rounded-xl hover:bg-orange-500 text-sm"
            >
              Restart
            </button>
          </div>
          {/* QC Photo Thumbnails (Expert Mode) */}
          {qcPhotos.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {qcPhotos.map((photo, i) => (
                <img key={i} src={photo.url} alt={`QC ${i + 1}`} className="w-16 h-16 rounded-lg object-cover flex-shrink-0 border-2 border-green-500" />
              ))}
              <span className="text-green-400 text-xs self-center ml-1">{qcPhotos.length} photo{qcPhotos.length !== 1 ? 's' : ''} saved</span>
            </div>
          )}
        </div>
      ) : (
        /* Production Mode: Complete when all steps checked */
        <div className="bg-gray-800 p-4 space-y-3">
          <button
            onClick={handleCompleteAllSteps}
            disabled={completingAll || !allStepsDone || isCompleted}
            className={`w-full py-6 text-white text-2xl font-bold rounded-xl flex items-center justify-center gap-3 transition-colors ${
              isCompleted
                ? 'bg-green-800 cursor-not-allowed'
                : !allStepsDone
                  ? 'bg-gray-600 cursor-not-allowed'
                  : completingAll
                    ? 'bg-green-700'
                    : 'bg-green-600 hover:bg-green-700 active:bg-green-800'
            }`}
          >
            {completingAll ? (
              'Completing...'
            ) : isCompleted ? (
              <>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                COMPLETED
              </>
            ) : !allStepsDone ? (
              `${card?.allSteps ? card.allSteps.filter(s => s.applicable !== false && !productionStepChecks[s.stepNumber]).length : 0} steps remaining`
            ) : (
              <>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                COMPLETE
              </>
            )}
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => setShowProblemModal(true)}
              className="flex-1 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 text-sm"
            >
              Problem
            </button>
            <button
              onClick={handlePrintLabel}
              disabled={labelPrinting}
              className="flex-1 py-3 bg-yellow-500 text-gray-900 font-medium rounded-xl hover:bg-yellow-400 disabled:opacity-50 flex items-center justify-center gap-1 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              {labelPrinting ? '...' : 'Print'}
            </button>
            {currentWs && DROP_LABEL_CODES.includes(currentWs.code) && (
              <button
                onClick={() => setShowDropModal(true)}
                className="flex-1 py-3 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-500 flex items-center justify-center gap-1 text-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.477l-3.763 1.105 1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.789l1.599.799L9 4.323V3a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Label Drop
              </button>
            )}
            <button
              onClick={() => qcFileInputRef.current?.click()}
              disabled={qcUploading}
              className="flex-1 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-500 disabled:opacity-50 flex items-center justify-center gap-1 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {qcUploading ? '...' : 'QC Photo'}
            </button>
            <button
              onClick={handleRestartStep}
              className="flex-1 py-3 bg-orange-600 text-white font-medium rounded-xl hover:bg-orange-500 text-sm"
            >
              Restart
            </button>
          </div>
          {/* QC Photo Thumbnails (Production Mode) */}
          {qcPhotos.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {qcPhotos.map((photo, i) => (
                <img key={i} src={photo.url} alt={`QC ${i + 1}`} className="w-16 h-16 rounded-lg object-cover flex-shrink-0 border-2 border-green-500" />
              ))}
              <span className="text-green-400 text-xs self-center ml-1">{qcPhotos.length} photo{qcPhotos.length !== 1 ? 's' : ''} saved</span>
            </div>
          )}
        </div>
      )}

      {/* Hidden QC Photo File Input */}
      <input
        ref={qcFileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleQcPhotoCapture}
        className="hidden"
      />

      {/* Break Buttons */}
      <BreakButtons onBreak={handleStartBreak} />

      {/* Quality Check Modal (training mode only) */}
      {showQualityModal && card?.currentStep?.quality_checks && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-white mb-4">Quality Check</h2>
            <div className="space-y-3 mb-6">
              {card.currentStep.quality_checks.map((check, index) => (
                <label key={index} className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={qualityChecks[check] || false}
                    onChange={(e) => setQualityChecks((prev) => ({ ...prev, [check]: e.target.checked }))}
                    className="w-6 h-6 rounded"
                  />
                  <span className="text-white">{check}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowQualityModal(false)} className="flex-1 py-3 bg-gray-600 text-white rounded-xl">
                Cancel
              </button>
              <button onClick={handleCompleteStep} className="flex-1 py-3 bg-green-600 text-white font-bold rounded-xl">
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Problem Modal */}
      {showProblemModal && <ProblemModal onClose={() => setShowProblemModal(false)} onSubmit={handleFlagStep} />}

      {/* Skip Modal */}
      {showSkipModal && <SkipModal onClose={() => setShowSkipModal(false)} onSubmit={handleSkipStep} />}

      {/* Drop Label Modal */}
      {showDropModal && currentWs && (
        <DropLabelModal
          wsCode={currentWs.code}
          orderNumber={card?.workOrder?.orderNumber || ''}
          frameColor={card?.orderSpecs?.frameColor || ''}
          onClose={() => setShowDropModal(false)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-32 left-4 right-4 p-4 rounded-xl shadow-lg z-50 ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          } text-white text-center font-medium`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

// Break Buttons Component
function BreakButtons({ onBreak }: { onBreak: (type: string, reason?: string) => void }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showPauseReasons, setShowPauseReasons] = useState(false);

  const pauseReasons = [
    { label: 'Customer', value: 'customer' },
    { label: 'Truck / Delivery', value: 'truck' },
    { label: 'Meeting', value: 'meeting' },
    { label: 'Other', value: 'other' },
  ];

  return (
    <>
      <button
        onClick={() => { setShowMenu(!showMenu); setShowPauseReasons(false); }}
        className="fixed bottom-24 right-4 w-14 h-14 bg-yellow-600 rounded-full flex items-center justify-center text-2xl shadow-lg z-40"
      >
        ☕
      </button>
      {showMenu && (
        <div className="fixed bottom-40 right-4 bg-gray-800 rounded-xl shadow-xl z-40 overflow-hidden">
          {!showPauseReasons ? (
            <>
              <button
                onClick={() => {
                  onBreak('lunch');
                  setShowMenu(false);
                }}
                className="w-full px-6 py-4 text-white hover:bg-gray-700 flex items-center gap-3"
              >
                <span className="text-2xl">🍽️</span> Lunch
              </button>
              <button
                onClick={() => {
                  onBreak('break');
                  setShowMenu(false);
                }}
                className="w-full px-6 py-4 text-white hover:bg-gray-700 flex items-center gap-3"
              >
                <span className="text-2xl">☕</span> Break
              </button>
              <button
                onClick={() => {
                  onBreak('restroom');
                  setShowMenu(false);
                }}
                className="w-full px-6 py-4 text-white hover:bg-gray-700 flex items-center gap-3"
              >
                <span className="text-2xl">🚻</span> Restroom
              </button>
              <button
                onClick={() => setShowPauseReasons(true)}
                className="w-full px-6 py-4 text-white hover:bg-gray-700 flex items-center gap-3 border-t border-gray-700"
              >
                <span className="text-2xl">⏸️</span> Pause
              </button>
            </>
          ) : (
            <>
              <div className="px-6 py-3 text-gray-400 text-sm font-medium border-b border-gray-700">Pause Reason</div>
              {pauseReasons.map((r) => (
                <button
                  key={r.value}
                  onClick={() => {
                    onBreak('pause', r.value);
                    setShowMenu(false);
                    setShowPauseReasons(false);
                  }}
                  className="w-full px-6 py-4 text-white hover:bg-gray-700 flex items-center gap-3"
                >
                  {r.label}
                </button>
              ))}
              <button
                onClick={() => setShowPauseReasons(false)}
                className="w-full px-6 py-3 text-gray-400 hover:bg-gray-700 text-sm border-t border-gray-700"
              >
                Back
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}

// Problem Modal Component
function ProblemModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (reason: string, notes: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const reasons = ['missing_parts', 'defect_found', 'wrong_specs', 'machine_issue', 'other'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-white mb-4">Report Problem</h2>
        <div className="space-y-3 mb-4">
          {reasons.map((r) => (
            <button
              key={r}
              onClick={() => setReason(r)}
              className={`w-full p-3 rounded-lg text-left ${
                reason === r ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300'
              }`}
            >
              {r.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
            </button>
          ))}
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional notes (optional)"
          className="w-full p-3 bg-gray-700 text-white rounded-lg mb-4"
          rows={3}
        />
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 bg-gray-600 text-white rounded-xl">
            Cancel
          </button>
          <button
            onClick={() => onSubmit(reason, notes)}
            disabled={!reason}
            className="flex-1 py-3 bg-red-600 text-white font-bold rounded-xl disabled:opacity-50"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

// Skip Modal Component
function SkipModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (reason: string) => void }) {
  const [reason, setReason] = useState('');

  const reasons = ['parts_not_ready', 'waiting_on_previous_step', 'need_supervisor', 'other'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-white mb-4">Skip This Step?</h2>
        <div className="space-y-3 mb-4">
          {reasons.map((r) => (
            <button
              key={r}
              onClick={() => setReason(r)}
              className={`w-full p-3 rounded-lg text-left ${
                reason === r ? 'bg-yellow-600 text-white' : 'bg-gray-700 text-gray-300'
              }`}
            >
              {r.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 bg-gray-600 text-white rounded-xl">
            Cancel
          </button>
          <button
            onClick={() => onSubmit(reason)}
            disabled={!reason}
            className="flex-1 py-3 bg-yellow-600 text-white font-bold rounded-xl disabled:opacity-50"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}

// Workstation → default part type mapping
const WS_DEFAULT_PART_TYPE: Record<string, string> = {
  SAW1: 'housing_main',
  SAW2: 'v_track',
  SAW3: 'roller_tube',
  SAW4: 'weight_bar',
  CUTTABLE: 'fabric',
};

// Part types relevant to each workstation
const WS_PART_TYPES: Record<string, { value: string; label: string }[]> = {
  SAW1: [
    { value: 'housing_main', label: 'Housing Main' },
    { value: 'housing_cover', label: 'Housing Cover' },
  ],
  SAW2: [
    { value: 'v_track', label: 'V-Track' },
    { value: 'u_channel', label: 'U-Channel' },
    { value: 'keder_guide', label: 'Keder Guide' },
    { value: 'screw_cover', label: 'Screw Cover' },
  ],
  SAW3: [{ value: 'roller_tube', label: 'Roller Tube' }],
  SAW4: [
    { value: 'weight_bar', label: 'Weight Bar' },
    { value: 'bottom_bar', label: 'Bottom Bar' },
  ],
  CUTTABLE: [
    { value: 'fabric', label: 'Fabric' },
    { value: 'keder', label: 'Keder Spline' },
  ],
};

const STOCK_LENGTHS: Record<string, number> = {
  housing_main: 288, v_track: 288, roller_tube: 288,
  weight_bar: 288, u_channel: 288, bottom_bar: 288,
};

const FRAME_COLORS = [
  { value: 'Black', label: 'Black' },
  { value: 'White', label: 'White' },
  { value: 'Bronze', label: 'Bronze' },
  { value: 'Tan', label: 'Tan' },
  { value: 'Mill Finish', label: 'Mill Finish' },
  { value: 'Custom', label: 'Custom Powder Coat' },
];

const KEDER_COLORS = [
  { value: 'Black', label: 'Black' },
  { value: 'White', label: 'White' },
];

function getDropColorOptions(partType: string) {
  if (!partType || partType === 'roller_tube' || partType === 'fabric') return null;
  if (partType === 'keder') return KEDER_COLORS;
  return FRAME_COLORS;
}

const PART_NUMBERS: Record<string, string> = {
  housing_main: '11001.0', housing_cover: '11001.1', v_track: '11002.0',
  roller_tube: '11003.0', weight_bar: '11004.0', u_channel: '11010.0',
  bottom_bar: '11005.0', screw_cover: '11012.0', keder_guide: '11011.0',
  keder: '11310',
};

// Drop Label Modal — creates drop + prints label without leaving the work card
function DropLabelModal({
  wsCode,
  orderNumber,
  frameColor: orderFrameColor,
  onClose,
}: {
  wsCode: string;
  orderNumber: string;
  frameColor?: string;
  onClose: () => void;
}) {
  const partTypes = WS_PART_TYPES[wsCode] || [];
  const defaultPt = WS_DEFAULT_PART_TYPE[wsCode] || '';
  const [partType, setPartType] = useState(defaultPt);
  const [length, setLength] = useState('');
  // Auto-fill color from order's frame color
  const [color, setColor] = useState(orderFrameColor || '');
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');

  const colorOptions = getDropColorOptions(partType);
  const partNumber = PART_NUMBERS[partType] || partType;
  const isKeder = partType === 'keder';

  const canSubmit = partType && length && (!colorOptions || color);

  const handlePrint = async () => {
    if (!canSubmit) return;
    setPrinting(true);
    setError('');

    try {
      const lengthNum = parseFloat(length);
      if (isNaN(lengthNum) || lengthNum <= 0) {
        setError('Invalid length');
        setPrinting(false);
        return;
      }

      // Create the drop
      const drop = await api.createDrop({
        partType,
        partNumber,
        lengthInches: lengthNum,
        originalStockLength: STOCK_LENGTHS[partType] || 288,
        orderNumber: orderNumber || undefined,
      });

      // Generate + print label
      const result = await api.generateDropLabel({
        dropId: drop.dropId,
        partType: drop.partType,
        length: drop.lengthInches || 0,
        partNumber: drop.partNumber,
        color: color || undefined,
      });
      await sendToMobiPrint(result.zpl);

      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create drop / print label');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md">
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Label Drop</h2>
            {orderNumber && <p className="text-sm text-gray-400">{orderNumber}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none">&times;</button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm">{error}</div>
          )}

          {/* Part type — pre-selected, changeable if station has multiple */}
          {partTypes.length > 1 ? (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Part Type</label>
              <select
                value={partType}
                onChange={e => { setPartType(e.target.value); setColor(''); }}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              >
                {partTypes.map(pt => (
                  <option key={pt.value} value={pt.value}>{pt.label}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="p-3 bg-gray-700 rounded-lg">
              <span className="text-gray-400 text-sm">Part Type:</span>
              <span className="text-white font-medium ml-2">{partTypes[0]?.label || partType}</span>
            </div>
          )}

          {/* Color */}
          {colorOptions && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Color</label>
              <select
                value={color}
                onChange={e => setColor(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                required
              >
                <option value="">Select color...</option>
                {colorOptions.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Length */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Length ({isKeder ? 'yards' : 'inches'})
            </label>
            <input
              type="number"
              value={length}
              onChange={e => setLength(e.target.value)}
              onWheel={e => e.currentTarget.blur()}
              placeholder={isKeder ? 'e.g., 10' : 'e.g., 96'}
              step="0.25"
              min="1"
              autoFocus
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 text-lg"
            />
            {!isKeder && length && parseFloat(length) < 84 && (
              <p className="text-yellow-400 text-sm mt-1">Below 84" — will be marked as waste</p>
            )}
          </div>

          <button
            onClick={handlePrint}
            disabled={printing || !canSubmit}
            className="w-full py-4 bg-yellow-500 text-gray-900 rounded-lg font-bold text-lg hover:bg-yellow-400 disabled:opacity-50"
          >
            {printing ? 'Creating & Printing...' : 'Print Drop Label'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WorkCardPage;
